// frontend/src/pages/dashboard/Taskhooks/useTaskSort.js
import { useState, useCallback, useMemo } from "react";

export const useTaskSort = (tasks) => {
  // ✅ ADD DEFAULT SORT CONFIG (latest tasks first)
  const [sortConfig, setSortConfig] = useState({
    field: 'created_at',      // ✅ Default sort field
    direction: 'desc'          // ✅ Descending (latest first)
  });

  const handleSort = useCallback((field) => {
    setSortConfig(prev => {
      // If clicking same field, toggle direction or reset
      if (prev.field === field) {
        if (prev.direction === 'asc') {
          // 2nd click: descending
          return { field, direction: 'desc' };
        } else {
          // 3rd click: reset to default (created_at desc)
          return { field: 'created_at', direction: 'desc' };
        }
      }
      // New field: start with ascending
      return { field, direction: 'asc' };
    });
  }, []);

  const sortedTasks = useMemo(() => {
    if (!sortConfig.field || !tasks.length) return tasks;
    
    const sorted = [...tasks].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.field) {
        case 'title':
          aVal = a.title?.toLowerCase() || '';
          bVal = b.title?.toLowerCase() || '';
          break;
        case 'project':
          aVal = a.project?.name?.toLowerCase() || '';
          bVal = b.project?.name?.toLowerCase() || '';
          break;
        case 'due_date':
          aVal = a.due_date ? new Date(a.due_date).getTime() : Infinity;
          bVal = b.due_date ? new Date(b.due_date).getTime() : Infinity;
          break;
        case 'priority':
          const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
          aVal = priorityOrder[a.priority] ?? 999;
          bVal = priorityOrder[b.priority] ?? 999;
          break;
        case 'status':
          aVal = a.statusObj?.name?.toLowerCase() || '';
          bVal = b.statusObj?.name?.toLowerCase() || '';
          break;
        // ✅ ADD CREATED_AT SORTING
        case 'created_at':
          aVal = a.created_at ? new Date(a.created_at).getTime() : 0;
          bVal = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        // ✅ ADD UPDATED_AT SORTING
        case 'updated_at':
          aVal = a.updated_at ? new Date(a.updated_at).getTime() : 0;
          bVal = b.updated_at ? new Date(a.updated_at).getTime() : 0;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [tasks, sortConfig]);

  const clearSort = useCallback(() => {
    // ✅ Reset to default (created_at desc)
    setSortConfig({ field: 'created_at', direction: 'desc' });
  }, []);

  return {
    sortConfig,
    handleSort,
    sortedTasks,
    clearSort
  };
};